import { useState, useEffect, useCallback, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { io } from 'socket.io-client';
import { formatDate, formatCurrency } from '../regureStyles';
import { useLocale } from '../../utils/LocaleContext';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';
const SOCKET_URL = process.env.REACT_APP_BASE_SOCKET_URL || 'http://localhost:8080';

// Map API claim document to display format
function toDisplayClaim(c, t) {
  const assigned = c.assignedTo;
  const assignedName = assigned?.name || null;
  const assignedInitials = assignedName
    ? assignedName
        .split(' ')
        .map(p => p[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : null;
  const lobLabels = {
    professional_liability: t('screens.claimsScreen.lobProfessionalLiability'),
    daily_sickness: t('screens.claimsScreen.lobDailySickness'),
    legal_protection: t('screens.claimsScreen.lobLegalProtection'),
    property: t('screens.claimsScreen.lobProperty'),
    workers_comp: t('screens.claimsScreen.lobWorkersComp'),
    auto: t('screens.claimsScreen.lobAuto'),
    health: t('screens.claimsScreen.lobHealth'),
    casualty: t('screens.claimsScreen.lobCasualty'),
    commercial: t('screens.claimsScreen.lobCommercial'),
  };
  return {
    id: c.claimNumber || c._id,
    claimant: c.claimant?.name || '—',
    company: c.claimant?.company || '—',
    lob: lobLabels[c.lineOfBusiness] || c.lineOfBusiness || '—',
    status: c.status || 'new',
    priority: c.priority || 'medium',
    assigned: assignedName,
    assignedInitials,
    lossDate: c.lossDate ? formatDate(c.lossDate) : '—',
    reserve: c.reserveAmount ? formatCurrency(c.reserveAmount) : '—',
    // UPDATED is an ordinary operational date in a table, next to LOSS
    // DATE. formatDateLong takes its month name from the UI LANGUAGE, so
    // on a DD/MM/YYYY tenant this column read "Aug 13, 2026" beside a
    // "31/05/2026" in the very next cell. A field value follows the
    // tenant's configured format; only prose keeps a month name.
    updated: c.lastActivityAt ? formatDate(c.lastActivityAt) : '—',
    _id: c._id,
  };
}

const S = {
  burgundy: '#800020',
  burgundy2: '#722f37',
  textPrimary: '#1e293b',
  textSecondary: '#475569',
  textMuted: '#64748b',
  borderLight: '#e2e8f0',
  bgCard: '#f8fafc',
  bgHover: '#f1f5f9',
};

const useClaimFunctions = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const [claims, setClaims] = useState([]);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [updateCount, setUpdateCount] = useState(0);
  const [assignees, setAssignees] = useState([]);
  // X-02: page size user-controllable via PageSizeSelector.
  const [PAGE_SIZE, setPageSize] = useState(20);
  const socketRef = useRef(null);

  const userLogin = useSelector(state => state.userLogin);
  const currentUser = userLogin?.userInfo?.data?.user;
  const orgId = currentUser?.org || currentUser?._id;
  const loggedinUser = useSelector(state => state.loggedinUser);
  const userRole = loggedinUser?.userInfo?.data?.user?.role || 'adjuster';

  useLocale(); // reactive re-render on locale change

  const fetchClaims = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page, limit: PAGE_SIZE });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (assigneeFilter !== 'all') params.set('assignedTo', assigneeFilter);
      if (dateFromFilter) params.set('dateFrom', dateFromFilter);
      if (dateToFilter) params.set('dateTo', dateToFilter);
      if (searchQuery) params.set('search', searchQuery);
      const resp = await axios.get(`${BASE_URL}/claims?${params}`, { withCredentials: true });
      if (resp.data?.data) {
        setClaims(resp.data.data.map(c => toDisplayClaim(c, t)));
        setTotal(resp.data.total || resp.data.data.length);
        setUpdateCount(0);
      }
    } catch {
      // show empty state on error
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, searchQuery, assigneeFilter, dateFromFilter, dateToFilter, t, PAGE_SIZE]);

  useEffect(() => {
    fetchClaims();
  }, [fetchClaims]);

  // ── Fetch assignees for filter dropdown ─────────────────
  useEffect(() => {
    const fetchAssignees = async () => {
      try {
        const resp = await axios.get(`${BASE_URL}/claims/assignees`, { withCredentials: true });
        if (resp.data?.data && Array.isArray(resp.data.data)) {
          setAssignees(resp.data.data);
        }
      } catch {
        // fail silently, assignee filter will just show all/unassigned
      }
    };
    fetchAssignees();
  }, []);

  // ── Socket: live update banner ───────────────────────────
  useEffect(() => {
    if (!orgId) return;
    const socket = io(SOCKET_URL, {
      query: { id: currentUser?._id },
      transports: ['websocket', 'polling'],
      reconnectionAttempts: 5,
    });
    socketRef.current = socket;
    socket.on('connect', () => socket.emit('join:org', String(orgId)));

    const bumpCount = () => setUpdateCount(prev => prev + 1);

    socket.on('dashboard:claim:created', data => {
      bumpCount();
      // Optimistically prepend the new claim to the top of the visible list
      setClaims(prev => {
        const entry = {
          id: data.claimNumber || '—',
          claimant: data.claimant?.name || '—',
          company: '—',
          lob: '—',
          status: data.status || 'new',
          priority: data.priority || 'medium',
          assigned: data.actor?.name || null,
          assignedInitials: data.actor?.name ? data.actor.name.slice(0, 2).toUpperCase() : null,
          updated: 'just now',
          _id: data.claimId,
        };
        return [entry, ...prev].slice(0, PAGE_SIZE);
      });
    });

    socket.on('dashboard:claim:statusChanged', data => {
      // In-place badge update
      setClaims(prev => prev.map(c => (c.id === data.claimNumber ? { ...c, status: data.to, updated: 'just now' } : c)));
      bumpCount();
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = claims; // filtering done server-side when API available

  return {
    filtered,
    userRole,
    assignees,
    updateCount,
    loading,
    page,
    setPage,
    dateToFilter,
    setDateToFilter,
    dateFromFilter,
    setDateFromFilter,
    assigneeFilter,
    setAssigneeFilter,
    statusFilter,
    setStatusFilter,
    searchQuery,
    setSearchQuery,
    total,
    history,
    claims,
    fetchClaims,
    PAGE_SIZE,
    setPageSize: (n) => { setPageSize(n); setPage(1); },
    S,
  };
};

export default useClaimFunctions;
